import { useState, useRef, ChangeEvent, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Alert,
  Stack,
  Button,
  Tooltip,
  Snackbar,
  Typography,
  IconButton
} from "@material-ui/core";
import { Close, Upload, Star, StarBorder, DragIndicator } from "@material-ui/icons";
import LightboxModal from "components/LightboxModal";
import { ImagePreview } from "../../../../../@types/listings/imageData";
import type { MotionProps } from "framer-motion";

export const variants: MotionProps = {
  initial: { opacity: 0, scale: 0.5 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3 }
  },
  exit: { opacity: 0, scale: 0.5 }
};

type ListingImagesUploadProps = {
  loading: boolean;
  snackbarOpen: boolean;
  imagesPreview: ImagePreview[];
  removeImage: (targetId: string) => () => void;
  handleImageUpload: ({ target: { files } }: ChangeEvent<HTMLInputElement>) => void;
  handleSnackbarClose: () => void;
  // Persist a new image order. The first image in the array is treated as the
  // "main"/cover image on the public listing page (cover = pictures[0]).
  onReorder: (reordered: ImagePreview[]) => void;
};

export function ListingImagesUpload({
  loading,
  snackbarOpen,
  imagesPreview,
  removeImage,
  handleImageUpload,
  handleSnackbarClose,
  onReorder
}: ListingImagesUploadProps): JSX.Element {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightBoxOpen, setLightBoxOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const { t: translate } = useTranslation();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleClickInput = (): void => inputFileRef.current?.click();

  const handleLightboxOpen = (imageIndex: number) => (): void => {
    setActiveImageIndex(imageIndex);
    setLightBoxOpen(true);
  };

  const handleLightboxClose = (): void => setLightBoxOpen(false);

  const moveImage = (from: number, to: number): void => {
    if (from === to || from < 0 || to < 0) return;
    if (from >= imagesPreview.length || to >= imagesPreview.length) return;

    const reordered = [...imagesPreview];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    onReorder(reordered);
  };

  // Live reorder: as the dragged thumbnail passes over another, the list
  // reorders immediately (the others shift to make room) and we track the
  // dragged item's new index. Nothing is persisted here — the new order lives
  // in component state and is saved when the listing form is submitted.
  // Setting dataTransfer is required for the drag to start at all in Firefox.
  const handleDragStart = (index: number) => (event: DragEvent<HTMLDivElement>): void => {
    setDragIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  // `preventDefault` on dragOver is what makes the element a valid drop target.
  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (index: number) => (): void => {
    if (dragIndex === null || dragIndex === index) return;

    moveImage(dragIndex, index);
    setDragIndex(index);
  };

  const handleDragEnd = (): void => setDragIndex(null);

  const setAsMain = (index: number) => (): void => moveImage(index, 0);

  const images = imagesPreview.map(({ src }) => src);

  return (
    <Stack gap={2}>
      <input
        style={{ display: "none" }}
        type="file"
        accept="image/*"
        ref={inputFileRef}
        multiple
        onChange={handleImageUpload}
      />
      <LightboxModal
        images={images}
        isOpen={lightBoxOpen}
        photoIndex={activeImageIndex}
        onClose={handleLightboxClose}
        setPhotoIndex={setActiveImageIndex}
      />
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: "100%" }}>
          {translate("listingsPage.warningImagesLimit")}
        </Alert>
      </Snackbar>
      <Button
        onClick={handleClickInput}
        variant="contained"
        startIcon={<Upload />}
        disabled={loading}
      >
        {translate("listingsPage.uploadImages")}
      </Button>
      {!!imagesPreview?.length && (
        <>
          <Typography variant="caption" color="textSecondary" textAlign="center">
            {translate("listingsPage.reorderImagesHint")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1}>
            {imagesPreview.map(({ id, src, alt }, index) => {
              const isMain = index === 0;

              return (
                <div
                  style={{
                    position: "relative",
                    cursor: loading ? "default" : "grab",
                    opacity: dragIndex === index ? 0.4 : 1,
                    transition: "opacity 200ms"
                  }}
                  key={id}
                  draggable={!loading}
                  onDragStart={handleDragStart(index)}
                  onDragEnter={handleDragEnter(index)}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                    <Box
                      sx={{
                        width: 128,
                        height: 128,
                        border: 2,
                        cursor: "pointer",
                        objectFit: "cover",
                        flexShrink: 0,
                        borderColor: isMain ? "primary.main" : "background.neutral",
                        borderRadius: 1,
                        backgroundColor: "background.neutral",
                        transition: "filter 200ms, border-color 200ms",
                        "&:hover": {
                          filter: "brightness(0.75)"
                        }
                      }}
                      src={src}
                      alt={alt}
                      loading="lazy"
                      component="img"
                      draggable={false}
                      onClick={handleLightboxOpen(index)}
                    />
                    <DragIndicator
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        left: 4,
                        color: "common.white",
                        filter: "drop-shadow(0 0 2px rgba(0,0,0,0.6))",
                        pointerEvents: "none"
                      }}
                      fontSize="small"
                    />
                    {isMain ? (
                      <Chip
                        icon={<Star sx={{ fontSize: 14 }} />}
                        label={translate("listingsPage.mainImage")}
                        size="small"
                        color="primary"
                        sx={{ position: "absolute", top: 4, left: 4, height: 22 }}
                      />
                    ) : (
                      <Tooltip title={translate("listingsPage.setAsMainImage")}>
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 4,
                            left: 4,
                            bgcolor: "rgba(255,255,255,0.85)",
                            "&:hover": { bgcolor: "rgba(255,255,255,1)" }
                          }}
                          size="small"
                          color="primary"
                          onClick={setAsMain(index)}
                          disabled={loading}
                        >
                          <StarBorder fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove">
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.85)",
                          "&:hover": { bgcolor: "rgba(255,255,255,1)" }
                        }}
                        size="small"
                        color="error"
                        onClick={removeImage(id)}
                        disabled={loading}
                      >
                        <Close />
                      </IconButton>
                    </Tooltip>
                </div>
              );
            })}
          </Stack>
        </>
      )}
    </Stack>
  );
}
